import React, { useEffect, useState } from "react";
import OrganizationAxiosInstance from "../Services/OrganizationAxiosInstance";
import PassTemplate from "../Modals/PassTemplate";
import { useParams, useNavigate } from "react-router-dom";
import ErrorAlert from "../Components/Error";
import Spinner from "../Components/Spinner";
import PhoneWithPass from "../Components/PhoneWithPass";
import { Bounce, toast } from "react-toastify";
import { isAxiosError } from "axios";
import Location from "../Modals/Location";
import MapForm from "../Components/MapForm";
import DesignForm from "../Components/DesignForm";
import LinksForm from "../Components/LinksForm";
import LocationsForm from "../Components/LocationsForm";
import DetailsForm from "../Components/DetailsForm";
import { useTranslation } from "react-i18next";
import {
  HiListBullet, HiPaintBrush, HiLink, HiMapPin,
  HiArrowUpTray, HiCheckCircle, HiXMark,
} from "react-icons/hi2";
import { FaWhatsapp } from "react-icons/fa";

type TabId = "details" | "design" | "links" | "locations";
type Folder = "logos" | "icons" | "points" | "StripeBackgroundImage";

const tabs: { id: TabId; icon: React.ReactNode; labelKey: string }[] = [
  { id: "details",   icon: <HiListBullet className="w-4 h-4" />,   labelKey: "details" },
  { id: "design",    icon: <HiPaintBrush className="w-4 h-4" />,   labelKey: "design" },
  { id: "links",     icon: <HiLink className="w-4 h-4" />,         labelKey: "links" },
  { id: "locations", icon: <HiMapPin className="w-4 h-4" />,       labelKey: "locations" },
];

const EditPassTemplate: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [passTemplate, setPassTemplate] = useState<PassTemplate | null>(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabId>("details");
  const [newLinkName, setNewLinkName] = useState("");
  const [newLinkUrl, setNewLinkUrl] = useState("");
  const [modalHidden, setModalHidden] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showPushInfo, setShowPushInfo] = useState(true);

  const GoogleMapsAPI = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  function getImagePath(url: string) {
    const parts = new URL(url).pathname.split("/");
    return `${process.env.REACT_APP_HOST}Images/${parts[parts.length - 2]}/${parts[parts.length - 1]}`;
  }

  function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      if (!file.type.startsWith("image/")) { reject(new Error("Not an image.")); return; }
      const img = new globalThis.Image();
      img.onload = () => { URL.revokeObjectURL(img.src); resolve({ width: img.width, height: img.height }); };
      img.onerror = () => { URL.revokeObjectURL(img.src); reject(new Error("Could not read image.")); };
      img.src = URL.createObjectURL(file);
    });
  }

  async function validateImageForFolder(file: File, folder: Folder): Promise<void> {
    if (file.type !== "image/png" && file.type !== "image/jpeg") throw new Error(t("Wrong File Type"));
    const limits: Record<Folder, { w: number; h: number; label: string }> = {
      logos: { w: 480, h: 150, label: "480x150px" },
      icons: { w: 87, h: 87, label: "87x87px" },
      StripeBackgroundImage: { w: 1125, h: 432, label: "1125x432px" },
      points: { w: 200, h: 200, label: "200x200px" },
    };
    const { w, h, label } = limits[folder];
    const { width, height } = await getImageDimensions(file);
    if (width > w || height > h) throw new Error(t("imageTooLarge", { label }));
  }

  async function uploadImage(img: File, folder: Folder) {
    try { await validateImageForFolder(img, folder); }
    catch (e) {
      toast.error(e instanceof Error ? e.message : "Invalid image.", { position: "top-right", theme: "light" });
      return null;
    }
    const formData = new FormData();
    formData.append("Img", img);
    formData.append("Folder", folder);
    const toastId = toast.loading("Uploading image…", { position: "top-right", theme: "light", transition: Bounce });
    try {
      const response = await OrganizationAxiosInstance.post("/Images/", formData, { headers: { "Content-Type": "multipart/form-data" } });
      if (response.status === 200) {
        toast.update(toastId, { render: t("passUpdatedSuccessfully") as string, type: "success", isLoading: false, autoClose: 3000 });
        return response;
      }
      toast.update(toastId, { render: t("couldntUploadImage") as string, type: "error", isLoading: false, autoClose: 3000 });
      return null;
    } catch (err) {
      toast.update(toastId, { render: isAxiosError(err) ? err.response?.data?.error || "Upload failed." : "Upload failed.", type: "error", isLoading: false, autoClose: 3000 });
      return null;
    }
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>, field: keyof PassTemplate, folder: Folder) => {
    const file = e.target.files?.[0];
    if (file && passTemplate) {
      const response = await uploadImage(file, folder);
      if (response?.data?.url) setPassTemplate({ ...passTemplate, [field]: response.data.url });
    }
  };

  const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>, field: keyof PassTemplate) => {
    if (passTemplate) setPassTemplate({ ...passTemplate, [field]: e.target.value });
  };

  function removeStripeImage() {
    setPassTemplate(prev => prev ? { ...prev, stripeBackgroundImage: "" } : prev);
  }

  function addLink(name: string, url: string) {
    if (!passTemplate) return;
    setPassTemplate({ ...passTemplate, links: [...(passTemplate.links || []), { name, url, id: undefined, passTemplateId: passTemplate.id }] });
    setNewLinkName(""); setNewLinkUrl("");
  }

  function removeLink(index: number) {
    if (!passTemplate) return;
    setPassTemplate({ ...passTemplate, links: passTemplate.links.filter((_, i) => i !== index) });
  }

  function onNotificationChange(index: number, message: string) {
    if (!passTemplate) return;
    setPassTemplate({ ...passTemplate, locations: passTemplate.locations.map((loc, i) => i === index ? { ...loc, autoNotification: message } : loc) });
  }

  function removeLocation(index: number) {
    if (!passTemplate) return;
    setPassTemplate({ ...passTemplate, locations: passTemplate.locations.filter((_, i) => i !== index) });
  }

  const handleAddLocation = (newLocation: Location) => {
    if (!passTemplate) return;
    newLocation.id = undefined;
    setPassTemplate({ ...passTemplate, locations: [...(passTemplate.locations || []), newLocation] });
    setModalHidden(true);
  };

  useEffect(() => {
    const fetch = async () => {
      try {
        const response = await OrganizationAxiosInstance.get("/PassTemplate/" + id);
        setPassTemplate(response.data);
      } catch {
        setError(t("somethingWentWrong"));
      } finally {
        setIsLoading(false);
      }
    };
    fetch();
  }, [id, refreshKey]);

  const apiCall = async (action: () => Promise<any>, loadingMsg: string, successMsg: string, onSuccess?: () => void) => {
    const toastId = toast.loading(loadingMsg, { position: "top-right", theme: "light", transition: Bounce });
    try {
      const res = await action();
      if (res.status === 200) {
        toast.update(toastId, { render: successMsg, type: "success", isLoading: false, autoClose: 3000 });
        onSuccess?.();
        setRefreshKey(k => k + 1);
      }
    } catch (err) {
      toast.update(toastId, { render: isAxiosError(err) ? err.response?.data?.error || "Error" : "Error", type: "error", isLoading: false, autoClose: 3000 });
    }
  };

  const UpdateDesign = () => apiCall(
    () => OrganizationAxiosInstance.put(`/PassTemplate/${passTemplate?.id}/Design`, {
      BackgroundColor: passTemplate?.backgroundColor, TextColor: passTemplate?.textColor,
      StripeBackgroundColor: passTemplate?.stripeBackgroundColor, stripeBackgroundImage: passTemplate?.stripeBackgroundImage,
      Icon: passTemplate?.icon, logo: passTemplate?.logo, pointImage: passTemplate?.pointImage,
    }),
    t("updatingPassDesign"), t("passUpdatedSuccessfully"), () => setActiveTab("links"),
  );

  const UpdateBase = (
    name: string, description: string, pointsToCollect: number, rewardName: string,
    rewardType: "Stamp" | "CouponValue", couponValue: number | null, currencyCode: string | null,
  ) => apiCall(
    () => OrganizationAxiosInstance.put(`/PassTemplate/${passTemplate?.id}/Base`, {
      Name: name, Description: description, Rewardname: rewardName, PointsToCollect: pointsToCollect,
      RewardType: rewardType, CouponValue: couponValue, CurrencyCode: currencyCode,
    }),
    t("updatingPassDesign"), t("passUpdatedSuccessfully"), () => setActiveTab("design"),
  );

  const UpdateLinks = () => apiCall(
    () => OrganizationAxiosInstance.put(`/PassTemplate/${passTemplate?.id}/Links`, passTemplate?.links),
    t("UpdatingPassLinks"), t("passLinksUpdatedSuccessfully"), () => setActiveTab("locations"),
  );

  const UpdateLocations = () => apiCall(
    () => OrganizationAxiosInstance.put(`/PassTemplate/${passTemplate?.id}/Locations`, passTemplate?.locations),
    t("UpdatingPassLocations"), t("PassLcoationsUpdatedSuccessfully"),
  );

  const PushUpdates = () => apiCall(
    () => OrganizationAxiosInstance.post(`/PassTemplate/${passTemplate?.id}/PushUpdateToPasses`),
    t("pushingUpdatesToAllPasses"), t("allPassessUpdatedSuccessfully"),
  );

  const Activate = () => apiCall(
    () => OrganizationAxiosInstance.get(`/PassTemplate/${passTemplate?.id}/Activate`),
    t("ActivatingProgram"), t("programActivatedSuccessfully"),
    () => navigate("/Dashboard/Program/" + passTemplate?.id),
  );

  const needsPush = passTemplate?.activated && passTemplate?.lastUpdated && passTemplate?.lastPushed
    && passTemplate.lastUpdated > passTemplate.lastPushed;

  if (isLoading) return <Spinner size={70} loading />;
  if (error) return <ErrorAlert message={error} />;

  return (
    <div className="space-y-4 max-w-3xl mx-auto">

      {/* Setup help banner (only when not yet activated) */}
      {!passTemplate?.activated && (
        <a href="https://wa.me/966553608481" target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-3 p-4 rounded-2xl bg-white border border-brand-100 shadow-sm hover:shadow-md transition group"
        >
          <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
            <FaWhatsapp className="w-5 h-5 text-green-600" />
          </div>
          <p className="text-sm font-semibold text-gray-700 group-hover:text-brand-600 transition flex-1">
            {t("Need Help Setting Up Everything We can help you just for 400 SAR we will design setup and teach you how to use the platform Contact Us ")}
          </p>
        </a>
      )}

      {/* Push updates banner */}
      {needsPush && (
        <div className="flex items-center justify-between gap-3 p-4 rounded-2xl bg-amber-50 border border-amber-200">
          <div className="flex items-center gap-3 min-w-0">
            <HiArrowUpTray className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-amber-800">{t("pushUpdates")}</p>
              {showPushInfo && <p className="text-xs text-amber-600 mt-0.5">{t("pressToPush")}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={PushUpdates} className="px-4 py-2 rounded-xl bg-amber-500 text-white text-sm font-bold hover:bg-amber-600 transition active:scale-95">
              {t("pushUpdates")}
            </button>
            {showPushInfo && (
              <button onClick={() => setShowPushInfo(false)} className="p-1 text-amber-400 hover:text-amber-600 transition">
                <HiXMark className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Activate banner */}
      {!passTemplate?.activated && (
        <div className="flex items-center justify-between gap-3 p-4 rounded-2xl bg-brand-50 border border-brand-200">
          <div className="flex items-center gap-3">
            <HiCheckCircle className="w-5 h-5 text-brand-600 flex-shrink-0" />
            <p className="text-sm font-semibold text-brand-800">{t("ActivateProgram")}</p>
          </div>
          <button onClick={Activate} className="px-4 py-2 rounded-xl bg-brand-600 text-white text-sm font-bold hover:bg-brand-700 transition active:scale-95 flex-shrink-0">
            {t("ActivateProgram")}
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4 items-start">

        {/* Form card */}
        <div className="order-last lg:order-first bg-white rounded-2xl border border-brand-100 shadow-sm overflow-hidden">

          {/* Tabs */}
          <div className="flex border-b border-gray-100 overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-3.5 text-sm font-semibold whitespace-nowrap border-b-2 transition flex-1 justify-center ${
                  activeTab === tab.id
                    ? "border-brand-600 text-brand-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200"
                }`}
              >
                {tab.icon}
                <span className="hidden sm:inline">{t(tab.labelKey)}</span>
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="p-5">
            <DetailsForm passTemplate={passTemplate} activeTab={activeTab} UpdateDetails={UpdateBase} />

            <DesignForm
              activeTab={activeTab}
              passTemplate={passTemplate}
              handleFileChange={handleFileChange}
              handleColorChange={handleColorChange}
              getImagePath={getImagePath}
              UpdateDesign={UpdateDesign}
              removeStripeImage={removeStripeImage}
            />

            <LinksForm
              activeTab={activeTab}
              passTemplate={passTemplate}
              newLinkName={newLinkName}
              newLinkUrl={newLinkUrl}
              setNewLinkName={setNewLinkName}
              setNewLinkUrl={setNewLinkUrl}
              addLink={addLink}
              removeLink={removeLink}
              UpdateLinks={UpdateLinks}
            />

            <LocationsForm
              activeTab={activeTab}
              passTemplate={passTemplate}
              setModalHidden={setModalHidden}
              removeLocation={removeLocation}
              UpdateLocations={UpdateLocations}
              onNotificationChange={onNotificationChange}
            />
          </div>
        </div>

        {/* Phone preview — top on mobile, sticky sidebar on desktop */}
        <div className="order-first lg:order-last lg:sticky lg:top-24 flex flex-col items-center gap-4 bg-white rounded-2xl border border-brand-100 shadow-sm p-5">
          {passTemplate && (
            <PhoneWithPass
              backgroundColor={passTemplate.backgroundColor}
              logoUrl={passTemplate.logo}
              pointImageUrl={passTemplate.pointImage}
              stripBackgroundColor={passTemplate.stripeBackgroundColor}
              stripBackgroundUrl={passTemplate.stripeBackgroundImage}
              textColor={passTemplate.textColor}
              pointsToCollect={passTemplate.pointsToCollect}
              points={3}
              radeemedRewards={1}
            />
          )}
          <p className="text-xs text-gray-400 text-center">{t("preview")}</p>
        </div>
      </div>

      {/* Add Location Modal */}
      {!modalHidden && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h3 className="text-base font-bold text-gray-900">{t("AddNewLocation")}</h3>
              <button onClick={() => setModalHidden(true)} className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100 transition">
                <HiXMark className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5">
              {GoogleMapsAPI && passTemplate?.id
                ? <MapForm onAddLocation={handleAddLocation} passTemplateId={passTemplate.id} GoogleMapsAPI={GoogleMapsAPI} />
                : <ErrorAlert message="Google Maps API key is missing." />
              }
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditPassTemplate;
